import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {ActionBase, ActionWithPayload} from '../../../helpers/reduxHelpers';
import {
  ApiExternalChannelTypes,
  ExternalChannelTypeModel,
} from '../../../models/api/externalChannelTypeModel';
import {fetchApiAsync} from '../../../services/http/http';
import {HttpError} from '../../../services/http/httpError';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {ResourceTableStatusesEnum} from '../../appTypes';
import {getCompanyUrl} from '../company/companySelectors';
import {getExternalChannelTypesAsyncStatus} from './externalChannelTypesSelectors';

/*
 * Action enum.
 */

export enum ExternalChannelTypesActionsEnum {
  FETCH_EXTERNAL_CHANNEL_TYPES = '@@data/externalChannelTypes/FETCH_EXTERNAL_CHANNEL_TYPES',
  FETCH_EXTERNAL_CHANNEL_TYPES_COMPLETED = '@@data/externalChannelTypes/FETCH_EXTERNAL_CHANNEL_TYPES_COMPLETED',
  FETCH_EXTERNAL_CHANNEL_TYPES_FAILED = '@@data/externalChannelTypes/FETCH_EXTERNAL_CHANNEL_TYPES_FAILED',
}

/*
 * Action Types.
 */

export interface FetchExternalChannelTypesAction extends ActionBase {
  type: ExternalChannelTypesActionsEnum.FETCH_EXTERNAL_CHANNEL_TYPES;
}

export interface FetchExternalChannelTypesCompletedAction
  extends ActionWithPayload<ReadonlyArray<ExternalChannelTypeModel>> {
  type: ExternalChannelTypesActionsEnum.FETCH_EXTERNAL_CHANNEL_TYPES_COMPLETED;
}

export interface FetchExternalChannelTypesFailedAction extends ActionBase {
  type: ExternalChannelTypesActionsEnum.FETCH_EXTERNAL_CHANNEL_TYPES_FAILED;
}

/*
 * Union Action type.
 */

export type ExternalChannelTypesAction =
  | FetchExternalChannelTypesAction
  | FetchExternalChannelTypesCompletedAction
  | FetchExternalChannelTypesFailedAction;

/*
 * Action Creators.
 */

function fetchExternalChannelTypesAction(): FetchExternalChannelTypesAction {
  return {
    type: ExternalChannelTypesActionsEnum.FETCH_EXTERNAL_CHANNEL_TYPES,
  };
}

function fetchExternalChannelTypesCompletedAction(
  externalChannelTypes: ReadonlyArray<ExternalChannelTypeModel>,
): FetchExternalChannelTypesCompletedAction {
  return {
    type: ExternalChannelTypesActionsEnum.FETCH_EXTERNAL_CHANNEL_TYPES_COMPLETED,
    payload: externalChannelTypes,
  };
}

function fetchExternalChannelTypesFailedAction(): FetchExternalChannelTypesFailedAction {
  return {
    type: ExternalChannelTypesActionsEnum.FETCH_EXTERNAL_CHANNEL_TYPES_FAILED,
  };
}

/*
 * Actions.
 */

function computeShouldFetchExternalChannelTypesAction(): ThunkAction<boolean> {
  return (_dispatch, getState) => {
    const {status, isStale} = getExternalChannelTypesAsyncStatus(getState());

    // If the store is marked as stale, we should refresh.
    if (isStale) {
      return true;
    }

    // If the status is either failed or initial state, we should fetch.
    if (status === ResourceTableStatusesEnum.INITIAL || status === ResourceTableStatusesEnum.FAILED) {
      return true;
    }

    // We can just rely on the redux state currently.
    return false;
  };
}

export function fetchExternalChannelTypesAsyncAction(): ThunkAsyncAction {
  return async (dispatch, getState) => {
    // Check if we should fetch.
    const shouldFetch = dispatch(computeShouldFetchExternalChannelTypesAction());
    if (!shouldFetch) {
      return;
    }

    // Dispatch loading state.
    dispatch(fetchExternalChannelTypesAction());

    try {
      const companyUrl = getCompanyUrl(getState());
      const externalChannelTypes = await fetchApiAsync(
        ApiExternalChannelTypes,
        `${companyUrl}/external_channel_types`,
      );

      dispatch(fetchExternalChannelTypesCompletedAction(externalChannelTypes));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      dispatch(fetchExternalChannelTypesFailedAction());
    }
  };
}
